import { useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { ROUTES } from "../App";
import { LuEyeOff as FaRegEyeSlash, LuEye as FaRegEye } from "react-icons/lu";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from '../constants/firebase';
import useMoveTo from '../constants/navigation';

const ResetPassword = () => {
    const navigate = useMoveTo();
    const [searchParams] = useSearchParams();
    const oobCode = searchParams.get('oobCode');

    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [showPasswords, setShowPasswords] = useState({
        password: false,
        confirmPassword: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError("Passwords don't match!");
            return;
        }

        if (!oobCode) {
            setError("Invalid reset link. Please request a new password reset email.");
            return;
        }

        setIsSubmitting(true);

        try {
            // Verify the reset code is valid
            const email = await verifyPasswordResetCode(auth, oobCode);

            // Complete the password reset
            await confirmPasswordReset(auth, oobCode, formData.password);

            // Redirect to login or show success message
            navigate(ROUTES.LOGIN, { state: { success: "Password reset successfully! You can now login with your new password." } });
        } catch (error) {
            console.error("Error resetting password:", error);
            setError(error.message || "Failed to reset password. The link may have expired. Please request a new password reset email.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-main w-full flex items-center justify-center px-4 sm:px-6 md:px-8 py-12">
            <div className="w-full max-w-md mt-20">
                {/* Header */}
                <div className="text-center mb-8">
                    <h3 className="text-2xl md:text-4xl font-bold text-white mb-2">
                        Reset Password
                    </h3>
                    <p className="text-sm md:text-base text-gray-300">
                        Set a new password for your account.
                    </p>
                    {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* New Password */}
                    <div className="relative">
                        <label htmlFor="password" className="sr-only">New Password</label>
                        <input
                            id="password"
                            name="password"
                            type={showPasswords.password ? "text" : "password"}
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="New Password"
                            className="input-main w-full pr-10 py-3"
                            required
                            minLength={6}
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                            onClick={() => togglePasswordVisibility('password')}
                        >
                            {showPasswords.password ? <FaRegEye size={20} /> : <FaRegEyeSlash size={20} />}
                        </button>
                    </div>

                    {/* Confirm Password */}
                    <div className="relative">
                        <label htmlFor="confirmPassword" className="sr-only">Confirm Password</label>
                        <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showPasswords.confirmPassword ? "text" : "password"}
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder="Confirm Password"
                            className="input-main w-full pr-10 py-3"
                            required
                            minLength={6}
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                            onClick={() => togglePasswordVisibility('confirmPassword')}
                        >
                            {showPasswords.confirmPassword ? <FaRegEye size={20} /> : <FaRegEyeSlash size={20} />}
                        </button>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        className="btn-primary w-full"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;